import React, { useState, useEffect, Suspense } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Map, Loader2, AlertCircle } from 'lucide-react';
import { loadMapboxLibraries, isMapboxLoaded } from '@/lib/mapbox-loader';
import { getAccommodationsByCourseId } from '@/features/accommodation/api';

interface TrailMapLoaderProps {
  courseId: string;
  className?: string;
  hoveredPoint?: {lat: number, lon: number, elevation: number, distance: number} | null;
}

/**
 * Loading states for dynamic Mapbox loading
 */
enum LoadingState {
  IDLE = 'idle',
  LOADING = 'loading',
  LOADED = 'loaded',
  ERROR = 'error'
}

/**
 * Lazy loaded TrailMap component
 * Only loaded when user requests to view the map
 */
const LazyTrailMap = React.lazy(() => 
  import('./TrailMap')
);

/**
 * Map loading placeholder with progressive enhancement
 */
const MapPlaceholder: React.FC<{
  onLoadMap: () => void;
  loading: boolean;
  error?: string;
}> = ({ onLoadMap, loading, error }) => (
  <Card className="w-full h-[400px] flex items-center justify-center bg-gradient-to-br from-green-50 to-blue-50">
    <CardContent className="text-center space-y-4 p-6">
      {error ? (
        <>
          <AlertCircle className="w-12 h-12 text-red-500 mx-auto" />
          <div>
            <h3 className="text-lg font-semibold text-red-700">맵 로딩 오류</h3>
            <p className="text-red-600 text-sm mt-1">{error}</p>
            <Button 
              variant="outline" 
              onClick={onLoadMap} 
              className="mt-3"
              size="sm"
            >
              다시 시도
            </Button>
          </div>
        </>
      ) : (
        <>
          <Loader2 className="w-12 h-12 text-blue-600 animate-spin mx-auto" />
          <div>
            <h3 className="text-lg font-semibold text-gray-800">
              맵 로딩 중...
            </h3>
            <p className="text-gray-600 text-sm mt-1">
              GPX 기반 지도를 불러오는 중입니다.
            </p>
          </div>
        </>
      )}
    </CardContent>
  </Card>
);

/**
 * Mapbox loading skeleton
 */
const MapSkeleton: React.FC = () => (
  <Card className="w-full h-[400px]">
    <CardContent className="p-0 h-full">
      <div className="w-full h-full bg-gradient-to-br from-gray-100 to-gray-200 rounded-lg relative overflow-hidden">
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="space-y-3">
            <Skeleton className="w-16 h-16 rounded-full mx-auto" />
            <Skeleton className="w-32 h-4" />
            <Skeleton className="w-24 h-3" />
          </div>
        </div>
        
        {/* Map-like decorative elements */}
        <div className="absolute top-4 left-4 space-y-2">
          <Skeleton className="w-8 h-8 rounded" />
          <Skeleton className="w-8 h-8 rounded" />
        </div>
        
        <div className="absolute bottom-4 right-4">
          <Skeleton className="w-20 h-6 rounded" />
        </div>
        
        {/* Trail path simulation */}
        <svg className="absolute inset-0 w-full h-full opacity-20">
          <path
            d="M 50 200 Q 150 150 250 180 Q 350 220 400 160"
            stroke="#10b981"
            strokeWidth="3"
            fill="none"
            strokeDasharray="10,5"
          />
        </svg>
      </div>
    </CardContent>
  </Card>
);

/**
 * TrailMap with dynamic Mapbox loading for bundle optimization
 * 
 * Benefits:
 * - Reduces main bundle by ~1.5MB (Mapbox GL JS)
 * - Loads map libraries only when needed
 * - Provides graceful loading states
 * - Maintains full functionality when loaded
 */
interface AccommodationPoint {
  accommodation_id: string;
  name: string;
  latitude: number | null;
  longitude: number | null;
  address?: string;
  main_image?: {
    image_url: string;
  } | null;
  accommodation_type?: {
    id: number;
    name: string;
  };
}

export const TrailMapLoader: React.FC<TrailMapLoaderProps> = (props) => {
  const [loadingState, setLoadingState] = useState<LoadingState>(
    isMapboxLoaded() ? LoadingState.LOADED : LoadingState.IDLE
  );
  const [error, setError] = useState<string>('');
  const [accommodations, setAccommodations] = useState<AccommodationPoint[]>([]);

  /**
   * Load Mapbox libraries on demand
   */
  const handleLoadMap = async () => {
    if (loadingState === LoadingState.LOADED || loadingState === LoadingState.LOADING) return;
    
    setLoadingState(LoadingState.LOADING);
    setError('');
    
    try {
      await loadMapboxLibraries();
      setLoadingState(LoadingState.LOADED);
    } catch (err) {
      console.error('Failed to load Mapbox:', err);
      setError(err instanceof Error ? err.message : '지도 라이브러리 로딩에 실패했습니다.');
      setLoadingState(LoadingState.ERROR);
    }
  };

  // Auto-load Mapbox on mount for immediate display
  useEffect(() => {
    if (isMapboxLoaded()) {
      setLoadingState(LoadingState.LOADED);
    } else {
      // 자동으로 Mapbox 로드 시작
      handleLoadMap();
    }
  }, []);

  // Load accommodations for the course
  useEffect(() => {
    const loadAccommodations = async () => {
      try {
        const data = await getAccommodationsByCourseId(props.courseId);

        // 중복 제거: accommodation_id 기준으로 unique한 데이터만 유지
        const uniqueAccommodations = data.reduce((acc, current) => {
          const exists = acc.find(item => item.accommodation_id === current.accommodation_id);
          if (!exists) {
            acc.push(current);
          }
          return acc;
        }, [] as typeof data);

        setAccommodations(uniqueAccommodations.map(acc => ({
          accommodation_id: acc.accommodation_id,
          name: acc.name,
          latitude: acc.latitude !== undefined && acc.latitude !== null ? Number(acc.latitude) : null,
          longitude: acc.longitude !== undefined && acc.longitude !== null ? Number(acc.longitude) : null,
          address: acc.address,
          main_image: acc.main_image,
          accommodation_type: acc.accommodation_type,
        })));
      } catch (err) {
        console.error('Failed to load accommodations:', err);
        // Don't set error state as accommodations are optional
      }
    };

    if (props.courseId) {
      loadAccommodations();
    }
  }, [props.courseId]);

  // Show placeholder during initial load or error
  if (loadingState !== LoadingState.LOADED) {
    return (
      <MapPlaceholder 
        onLoadMap={handleLoadMap}
        loading={loadingState === LoadingState.LOADING}
        error={loadingState === LoadingState.ERROR ? error : undefined}
      />
    );
  }

  // Show map with Suspense fallback for component lazy loading
  return (
    <Suspense fallback={<MapSkeleton />}>
      <LazyTrailMap {...props} accommodations={accommodations} />
    </Suspense>
  );
};